/**
 * Design 1: "Clean Split-Panel"
 * Mirrors the review page's two-column layout.
 * Citation on the left in the same serif style, reviewer decisions in a right sidebar.
 * Minimal, focused, Covidence-like.
 */
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Loader2,
  User,
} from "lucide-react";
import { Suspense, useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useProjectContext } from "~/components/layout/project-context";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogBackdrop,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogPortal,
  DialogTitle,
} from "~/components/ui/dialog";
import { Kbd } from "~/components/ui/kbd";
import { Label } from "~/components/ui/label";
import { ScrollArea } from "~/components/ui/scroll-area";
import { $submitAdjudication } from "~/lib/screening/adjudication";
import type { ConflictDetails } from "~/lib/screening/adjudication";
import {
  adjudicationQueueQueryOptions,
  conflictDetailsQueryOptions,
  exclusionCodesQueryOptions,
} from "~/lib/screening/queries";

const searchSchema = z.object({
  citationId: z.string().optional(),
});

export const Route = createFileRoute(
  "/_auth/projects/$projectId/screening/title-abstract/conflicts-v1",
)({
  validateSearch: searchSchema,
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(adjudicationQueueQueryOptions(params.projectId)),
      context.queryClient.ensureQueryData(exclusionCodesQueryOptions(params.projectId)),
    ]);
  },
  component: ConflictsDesign1,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatAuthorsList(authors: ConflictDetails["citation"]["authors"]): string[] {
  if (!authors || authors.length === 0) return ["Unknown authors"];
  return authors.map((a) => {
    if (!a.given) return a.family;
    const initials = a.given
      .split(" ")
      .map((p) => p.charAt(0))
      .join("");
    return `${a.family} ${initials}`;
  });
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function DecisionBadge({ decision }: { decision: string }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    include: { bg: "bg-emerald-50 dark:bg-emerald-950/40", text: "text-emerald-700 dark:text-emerald-300", label: "Include" },
    exclude: { bg: "bg-red-50 dark:bg-red-950/40", text: "text-red-700 dark:text-red-300", label: "Exclude" },
    maybe: { bg: "bg-amber-50 dark:bg-amber-950/40", text: "text-amber-700 dark:text-amber-300", label: "Maybe" },
  };
  const c = config[decision] ?? { bg: "bg-gray-100", text: "text-gray-700", label: decision };
  return (
    <span className={`${c.bg} ${c.text} inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold`}>
      {c.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
function ConflictsDesign1() {
  const { projectId, projectTitle } = useProjectContext();
  const { citationId: searchCitationId } = Route.useSearch();
  const navigate = useNavigate();

  const { data: queue } = useSuspenseQuery(adjudicationQueueQueryOptions(projectId));

  const currentCitationId = useMemo(() => {
    if (searchCitationId && queue.some((c) => c.id === searchCitationId)) return searchCitationId;
    return queue[0]?.id;
  }, [queue, searchCitationId]);

  const goToNext = useCallback(() => {
    navigate({
      to: "/projects/$projectId/screening/title-abstract/conflicts-v1",
      params: { projectId },
      search: {},
      replace: true,
    });
  }, [navigate, projectId]);

  if (queue.length === 0) {
    return (
      <div className="flex min-h-screen flex-col">
        <main className="flex flex-1 items-center justify-center p-6">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/30">
              <CheckCircle className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="mb-2 text-lg font-semibold">No Conflicts to Resolve</h2>
            <p className="text-muted-foreground mb-6 text-sm">
              All screening conflicts have been resolved.
            </p>
            <Button render={<Link to="/projects/$projectId" params={{ projectId }} />}>
              Return to Project
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden">
      {/* Screening-style header bar */}
      <div className="bg-background/95 supports-[backdrop-filter]:bg-background/60 border-b px-6 py-3 backdrop-blur">
        <div className="flex items-center justify-between gap-4">
          <span className="text-foreground text-sm tabular-nums">
            <AlertTriangle className="mr-1.5 inline h-3.5 w-3.5 text-amber-500" />
            {queue.length} conflict{queue.length === 1 ? "" : "s"} remaining
          </span>
        </div>
      </div>

      {currentCitationId && (
        <Suspense
          fallback={
            <div className="flex flex-1 items-center justify-center">
              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            </div>
          }
        >
          <AdjudicationContent
            projectId={projectId}
            citationId={currentCitationId}
            onResolved={goToNext}
          />
        </Suspense>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Adjudication content — two-column layout like the review page
// ---------------------------------------------------------------------------
function AdjudicationContent({
  projectId,
  citationId,
  onResolved,
}: {
  projectId: string;
  citationId: string;
  onResolved: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: conflict } = useSuspenseQuery(conflictDetailsQueryOptions(projectId, citationId));
  const { data: codes } = useSuspenseQuery(exclusionCodesQueryOptions(projectId));

  const [resolution, setResolution] = useState<"include" | "exclude" | null>(null);
  const [rationale, setRationale] = useState("");
  const [exclusionCode, setExclusionCode] = useState("");
  const [showExclusionDialog, setShowExclusionDialog] = useState(false);

  const adjudicateMutation = useMutation({
    mutationFn: (data: {
      resolution: "include" | "exclude";
      rationale: string;
      exclusionCode?: string;
    }) =>
      $submitAdjudication({
        data: {
          projectId,
          citationId,
          resolution: data.resolution,
          rationale: data.rationale,
          exclusionCode: data.exclusionCode,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "adjudication"] });
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "screening"] });
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "dual-screening"] });
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "statistics"] });
      toast.success("Adjudication submitted");
      setResolution(null);
      setRationale("");
      setExclusionCode("");
      onResolved();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const handleResolve = (sel: "include" | "exclude") => {
    if (sel === "exclude") {
      setResolution("exclude");
      setShowExclusionDialog(true);
    } else {
      setResolution(sel);
    }
  };

  const handleSubmit = () => {
    if (!resolution || !rationale.trim()) return;
    adjudicateMutation.mutate({
      resolution,
      rationale: rationale.trim(),
      exclusionCode: resolution === "exclude" ? exclusionCode : undefined,
    });
  };

  const handleExclusionSubmit = (code: string) => {
    setExclusionCode(code);
    setShowExclusionDialog(false);
  };

  const activeCodes = codes.filter((c) => c.isActive);
  const isPending = adjudicateMutation.isPending;
  const { citation, decision1, decision2 } = conflict;

  if (!conflict.canAdjudicate) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="max-w-md rounded-lg border border-amber-200 bg-amber-50 p-6 text-center dark:border-amber-900 dark:bg-amber-950/30">
          <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-amber-500" />
          <p className="font-medium text-amber-800 dark:text-amber-200">Cannot adjudicate this conflict</p>
          <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">{conflict.adjudicationReason}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Two-column layout */}
      <div className="flex min-h-0 flex-1">
        {/* Left: citation content */}
        <div className="flex min-h-0 flex-1 flex-col">
          <ScrollArea className="min-h-0 flex-1 overflow-hidden">
            <main className="px-6 py-8">
              <article className="mx-auto max-w-2xl animate-fade-in-up">
                {/* Title section — matching review page style */}
                <section className="mb-6">
                  <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                    Title
                  </span>
                  <h1 className="mt-1 font-serif text-3xl font-medium leading-tight">
                    {citation.title}
                  </h1>
                </section>

                {/* Abstract section — matching review page style */}
                <section>
                  <div className="mb-2 flex items-baseline justify-between">
                    <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                      Abstract
                    </span>
                    {citation.abstract && (
                      <span className="text-muted-foreground text-xs">
                        {countWords(citation.abstract)} words
                      </span>
                    )}
                  </div>
                  {citation.abstract ? (
                    <p className="text-[15px] leading-relaxed">{citation.abstract}</p>
                  ) : (
                    <p className="text-muted-foreground text-sm italic">No abstract available</p>
                  )}
                </section>
              </article>
            </main>
          </ScrollArea>
        </div>

        {/* Right sidebar — reviewer decisions + citation details */}
        <aside className="bg-muted/30 hidden w-80 shrink-0 border-l lg:block">
          <ScrollArea className="h-full">
            {/* Conflict notice */}
            <div className="border-b p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Reviewers disagree
              </div>
              <p className="text-muted-foreground mt-1 text-xs">
                Review both decisions and make a final determination.
              </p>
            </div>

            {/* Decision 1 */}
            <div className="border-b p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="bg-muted flex h-7 w-7 items-center justify-center rounded-full">
                  <User className="text-muted-foreground h-3.5 w-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{decision1.reviewerName}</p>
                  <p className="text-muted-foreground text-[10px]">First reviewer</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <DecisionBadge decision={decision1.decision} />
                {decision1.exclusionCode && (
                  <p className="text-muted-foreground text-xs">
                    Code:{" "}
                    <span className="bg-muted rounded px-1 py-0.5 font-mono text-[11px]">
                      {decision1.exclusionCode}
                    </span>
                  </p>
                )}
                {(decision1.exclusionNotes || decision1.notes) && (
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    {decision1.exclusionNotes || decision1.notes}
                  </p>
                )}
              </div>
            </div>

            {/* Decision 2 */}
            <div className="border-b p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="bg-muted flex h-7 w-7 items-center justify-center rounded-full">
                  <User className="text-muted-foreground h-3.5 w-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{decision2.reviewerName}</p>
                  <p className="text-muted-foreground text-[10px]">Second reviewer</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <DecisionBadge decision={decision2.decision} />
                {decision2.exclusionCode && (
                  <p className="text-muted-foreground text-xs">
                    Code:{" "}
                    <span className="bg-muted rounded px-1 py-0.5 font-mono text-[11px]">
                      {decision2.exclusionCode}
                    </span>
                  </p>
                )}
                {(decision2.exclusionNotes || decision2.notes) && (
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    {decision2.exclusionNotes || decision2.notes}
                  </p>
                )}
              </div>
            </div>

            {/* Citation details */}
            <div className="space-y-3 p-4">
              <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                Details
              </span>
              <div>
                <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Authors</span>
                <div className="mt-1 space-y-0.5 text-sm">
                  {formatAuthorsList(citation.authors).slice(0, 5).map((a, i) => (
                    <p key={i}>{a}</p>
                  ))}
                  {(citation.authors?.length ?? 0) > 5 && (
                    <p className="text-muted-foreground">+{(citation.authors?.length ?? 0) - 5} more</p>
                  )}
                </div>
              </div>
              {citation.year && (
                <div>
                  <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Year</span>
                  <p className="mt-1 text-sm">{citation.year}</p>
                </div>
              )}
              {citation.containerTitle && (
                <div>
                  <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Journal</span>
                  <p className="mt-1 text-sm">{citation.containerTitle}</p>
                </div>
              )}
              {(citation.volume || citation.issue) && (
                <div>
                  <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Volume / Issue</span>
                  <p className="mt-1 text-sm">
                    {citation.volume}{citation.issue && `(${citation.issue})`}
                  </p>
                </div>
              )}
              {citation.doi && (
                <div>
                  <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">DOI</span>
                  <p className="mt-1">
                    <a
                      href={`https://doi.org/${citation.doi}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary inline-flex items-center gap-1 text-xs break-all hover:underline"
                    >
                      {citation.doi}
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                  </p>
                </div>
              )}
              {citation.pmid && (
                <div>
                  <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">PMID</span>
                  <p className="mt-1">
                    <a
                      href={`https://pubmed.ncbi.nlm.nih.gov/${citation.pmid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary inline-flex items-center gap-1 text-xs hover:underline"
                    >
                      {citation.pmid}
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>
        </aside>
      </div>

      {/* Bottom action bar — matching review page style */}
      {!resolution ? (
        <div className="bg-background shrink-0 border-t p-4">
          <div className="mx-auto flex max-w-2xl items-center justify-center gap-3">
            <Button
              variant="destructive"
              className="text-background"
              onClick={() => handleResolve("exclude")}
              disabled={isPending}
            >
              Exclude
              <Kbd className="ml-2">N</Kbd>
            </Button>
            <Button
              className="bg-[#2B9A66] hover:bg-[#218358]"
              onClick={() => handleResolve("include")}
              disabled={isPending}
            >
              Include
              <Kbd className="ml-2">Y</Kbd>
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-background shrink-0 border-t p-4">
          <div className="mx-auto max-w-2xl">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <Label htmlFor="rationale" className="text-sm font-medium">
                  Rationale for{" "}
                  <span className={resolution === "include" ? "text-emerald-600" : "text-red-600"}>
                    {resolution === "include" ? "Include" : "Exclude"}
                  </span>
                  {resolution === "exclude" && exclusionCode && (
                    <span className="text-muted-foreground font-normal"> ({exclusionCode})</span>
                  )}
                </Label>
                <textarea
                  id="rationale"
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  placeholder="Explain your decision..."
                  rows={2}
                  className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring mt-2 flex w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                  autoFocus
                />
              </div>
              <div className="flex flex-col gap-2 pt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setResolution(null); setRationale(""); setExclusionCode(""); }}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSubmit}
                  disabled={!rationale.trim() || isPending}
                >
                  {isPending ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exclusion code dialog */}
      <Dialog open={showExclusionDialog} onOpenChange={setShowExclusionDialog}>
        <DialogPortal>
          <DialogBackdrop />
          <DialogPopup>
            <DialogHeader>
              <DialogTitle>Select Exclusion Reason</DialogTitle>
              <DialogDescription>Choose the reason for excluding this citation.</DialogDescription>
            </DialogHeader>
            <div className="mt-4 max-h-64 space-y-2 overflow-y-auto pr-1">
              {activeCodes.map((code) => (
                <div
                  key={code.id}
                  onClick={() => handleExclusionSubmit(code.code)}
                  className="hover:bg-muted/50 flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{code.label}</div>
                    {code.description && (
                      <div className="text-muted-foreground mt-0.5 text-xs">{code.description}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter className="mt-4">
              <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
            </DialogFooter>
          </DialogPopup>
        </DialogPortal>
      </Dialog>
    </>
  );
}
